import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../supabaseClient'
import {
    BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, PieChart, Pie, AreaChart, Area
} from "recharts"
import { useLoyaltyAIHistory } from '../../hooks/useLoyaltyAIHistory'
import { authHeaders } from '../../utils/authFetch'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

// const { data: { session }, error: sessionError } = await supabase.auth.getSession()
// console.log('session:', session, 'error:', sessionError)

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const TIER_COLORS = ['#B45309', '#0D9488', '#2563EB', '#7C3AED', '#DC2626', '#D97706']

const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
const toPHT = (isoStr) => new Date(new Date(isoStr).getTime() + PHT_OFFSET_MS)

const presetStart = (range) => {
    if (range === "all") return null
    const days = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000).toISOString()
}

// ─── Shared UI primitives ────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber, icon, loading = false }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <div className="flex items-center gap-1.5 mb-1">
            {icon && <span style={{ color }}>{icon}</span>}
            <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        </div>
        {loading ? (
            <SkeletonBlock className="h-6 w-16 mb-0.5" />
        ) : (
            <p className="mt-0 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        )}
        {sub && !loading && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: `linear-gradient(90deg, ${C.border} 0px, ${C.dark} 40px, ${C.border} 80px)`,
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
        <div className="rounded-lg px-3 py-2 text-xs border shadow-sm"
            style={{ background: '#fff', borderColor: C.border }}>
            <p className="m-0 mb-1.5 font-semibold" style={{ color: C.text }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="m-0 my-0.5" style={{ color: p.color || C.textSoft }}>
                    {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    )
}

const StatusBadge = ({ status }) => {
    const map = {
        completed: { bg: C.green + '22', color: C.green, label: 'Used' },
        pending: { bg: C.amber + '22', color: C.amber, label: 'Claimed' },
        cancelled: { bg: C.coral + '22', color: C.coral, label: 'Cancelled' },
        expired: { bg: C.muted + '22', color: C.muted, label: 'Expired' },
    }
    const cfg = map[status] ?? { bg: C.muted + '22', color: C.muted, label: status }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.label}
        </span>
    )
}

const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const options = [
        { label: "All Time", value: "all" },
        { label: "7 days",  value: "7d"  },
        { label: "30 days", value: "30d" },
        { label: "90 days", value: "90d" },
        { label: "1 year",  value: "1y"  },
        { label: "Custom",  value: "custom" },
    ]

    const handlePreset = (value) => {
        setRange(value)
        if (value !== 'custom') {
            setCustomStart("")
            setCustomEnd("")
        }
    }

    const hasCustom = customStart && customEnd
    const isCustomActive = range === 'custom' || hasCustom

    return (
        <div className="flex flex-col gap-2 items-end w-full sm:w-auto">
            <div className="flex gap-1.5 overflow-x-auto max-w-full pb-1" style={{ scrollbarWidth: 'thin' }}>
                {options.map((o) => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0 whitespace-nowrap"
                        style={{
                            background: range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color:       range === o.value && !hasCustom ? "#fff"  : C.textSoft,
                            cursor: "pointer",
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>
            
            {/* Custom date fields */}
            {isCustomActive && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 pb-1 border-t border-dashed"
                    style={{ borderColor: C.border }}>
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{
                            borderColor: hasCustom ? C.amber : C.border,
                            color: C.text,
                            background: C.card,
                        }}
                    />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input
                        type="date"
                        value={customEnd}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{
                            borderColor: hasCustom ? C.amber : C.border,
                            color: C.text,
                            background: C.card,
                        }}
                    />
                    {hasCustom && (
                        <button
                            onClick={() => { 
                                setCustomStart(""); 
                                setCustomEnd(""); 
                                setRange("30d")
                            }}
                            className="text-[11px] px-2 py-1 rounded-full border"
                            style={{ 
                                borderColor: C.border, 
                                color: C.muted, 
                                cursor: "pointer", 
                                background: C.card 
                            }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// ─── Pagination  ─────────────────────────────────────────────
function Pagination({ page, totalItems, pageSize, onPageChange }) {
    const totalPages = Math.ceil(totalItems / pageSize)
    if (totalPages <= 1) return null

    return (
        <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
            <span style={{ fontSize: 11, color: C.muted }}>
                {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems} items
            </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    style={{
                        padding: "3px 10px", borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.card,
                        color: page === 1 ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                    }}>
                    ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                        acc.push(n)
                        return acc
                    }, [])
                    .map((n, idx) =>
                        n === "…" ? (
                            <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                        ) : (
                            <button key={n} onClick={() => onPageChange(n)}
                                style={{
                                    width: 28, height: 28, borderRadius: 8,
                                    border: `1px solid ${n === page ? C.nude : C.border}`,
                                    background: n === page ? C.nude : C.card,
                                    color: n === page ? "#fff" : C.textSoft,
                                    fontSize: 11, fontWeight: n === page ? 600 : 400,
                                    cursor: "pointer",
                                }}>
                                {n}
                            </button>
                        )
                    )
                }
                <button
                    onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    style={{
                        padding: "3px 10px", borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.card,
                        color: page === totalPages ? C.border : C.textSoft,
                        fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                    }}>
                    Next →
                </button>
            </div>
        </div>
    )
}

// ─── Data hooks ──────────────────────────────────────────────────────────────
function useLoyaltyData() {
    const [tiers, setTiers] = useState(null)
    const [accounts, setAccounts] = useState(null)
    const [redemptions, setRedemptions] = useState(null)
    const [rewards, setRewards] = useState(null)

    useEffect(() => {
        const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
        fetch(`${BASE_URL}/api/loyalty-analytics/data`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => {
                setTiers(data.tiers ?? [])
                setAccounts(data.accounts ?? [])
                setRewards(data.rewards ?? [])
                setRedemptions(data.redemptions ?? [])
            })
            .catch(err => console.error('Error fetching loyalty analytics data:', err))
    }, [])

    return { tiers, accounts, redemptions, rewards }
}

// ─── Tier Member Distribution ─────────────────────────────────────────────
const TierDistributionPanel = ({ tiers, accounts, loading }) => {
    const [range, setRange] = useState("all")
    const [customStart, setCustomStart] = useState("")
    const [customEnd, setCustomEnd] = useState("")

    const startISO = useMemo(() => {
        if (customStart && customEnd) {
            return new Date(customStart).toISOString()
        }
        return presetStart(range)
    }, [customStart, customEnd, range])

    const endISO = useMemo(() => {
        if (customStart && customEnd) {
            return new Date(customEnd + "T23:59:59").toISOString()
        }
        return null
    }, [customStart, customEnd])

    const filteredAccounts = useMemo(() => {
        if (!accounts) return []
        return accounts.filter(a => {
            const d = new Date(a.joined_at)
            if (startISO && d < new Date(startISO)) return false
            if (endISO && d > new Date(endISO)) return false
            return true
        })
    }, [accounts, startISO, endISO])

    const tierData = useMemo(() => {
        if (!tiers || !filteredAccounts) return []
        
        const countMap = {}
        for (const acc of filteredAccounts) {
            if (!acc.current_tier_id) continue
            countMap[acc.current_tier_id] = (countMap[acc.current_tier_id] || 0) + 1
        }
        const untiered = filteredAccounts.filter(a => !a.current_tier_id).length

        const rows = tiers.map((t) => ({
            name: t.tier_name,
            count: countMap[t.tier_id] || 0,
            minPoints: t.points_required,
            color: t.tier_color,
        }))
        
        if (untiered > 0) {
            rows.push({ 
                name: 'No Tier', 
                count: untiered, 
                minPoints: 0, 
                color: C.muted 
            })
        }
        return rows
    }, [tiers, filteredAccounts])

    const total = tierData.reduce((s, t) => s + t.count, 0)
    const active = filteredAccounts.filter(a => a.is_active).length

        return (
            <Card>
                <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                    <SectionTitle
                        title="Tier Member Distribution"
                        subtitle="Members who joined within the selected period, by reward tier"
                    />
                    <RangeSelector
                        range={range}           setRange={setRange}
                        customStart={customStart} setCustomStart={setCustomStart}
                        customEnd={customEnd}     setCustomEnd={setCustomEnd}
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                    <KPI
                        label="Total Members"
                        value={total.toLocaleString()}
                        color={C.blue}
                        loading={loading}
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                            </svg>
                        }
                    />
                    <KPI
                        label="Active Members"
                        value={active.toLocaleString()}
                        color={C.green}
                        loading={loading}
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                        }
                    />
                    <KPI
                      label="Tiers Available"
                      value={tiers?.length ?? '—'}
                      color={C.amber}
                      loading={loading}
                      icon={
                        <svg 
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 30 40"
                          fill="currentColor"
                          >
                          <path d="M15 9.37687C18.9782 9.37687 22.7936 10.9572 25.6066 13.7703C28.4196 16.5833 30 20.3986 30 24.3769C30 28.3551 28.4196 32.1704 25.6066 34.9835C22.7936 37.7965 18.9782 39.3769 15 39.3769C11.0218 39.3769 7.20644 37.7965 4.3934 34.9835C1.58035 32.1704 0 28.3551 0 24.3769C0 20.3986 1.58035 16.5833 4.3934 13.7703C7.20644 10.9572 11.0218 9.37687 15 9.37687ZM15 15.9394L12.5213 20.9644L6.975 21.7706L10.9875 25.68L10.0406 31.2038L15 28.5956L19.9594 31.2019L19.0125 25.68L23.025 21.7688L17.4787 20.9625L15 15.9394ZM16.875 0L26.25 0.00187509V5.62687L23.6944 7.76062C21.5741 6.64709 19.2584 5.95435 16.875 5.72063V0ZM13.125 0V5.71875C10.7422 5.95271 8.42717 6.64544 6.3075 7.75875L3.75 5.62687V0.00187509L13.125 0Z"/>
                        </svg>
                      }
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Bar chart */}
                  <div>
                      <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>
                          Members per Tier
                      </p>
                      <ResponsiveContainer debounce={200} width="100%" height={260}>
                          <BarChart data={tierData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" name="Members" radius={[4, 4, 0, 0]}>
                                    {tierData.map((t, i) => <Cell key={i} fill={t.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Tier breakdown cards */}
                    <div className="flex flex-col gap-2">
                        <p className="text-[11px] uppercase tracking-wider m-0 mb-1" style={{ color: C.muted }}>
                            Tier Breakdown
                        </p>
                        {tierData.map((t) => {
                            const pct = total > 0 ? ((t.count / total) * 100).toFixed(0) : 0
                            return (
                                <div key={t.name} className="rounded-lg px-3 py-2 border"
                                    style={{ borderColor: C.border, background: C.dark }}>
                                    <div className="flex justify-between items-center mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                                            <span className="font-semibold text-xs" style={{ color: C.text }}>{t.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px]" style={{ color: C.muted }}>{t.count} members</span>
                                            <span className="font-bold text-xs" style={{ color: t.color }}>{pct}%</span>
                                        </div>
                                    </div>
                                    {t.minPoints > 0 && (
                                        <p className="m-0 text-[10px] mb-1" style={{ color: C.muted }}>
                                            Min {t.minPoints.toLocaleString()} pts required
                                        </p>
                                    )}
                                    <div className="h-1 rounded-full" style={{ background: C.border }}>
                                        <div className="h-1 rounded-full transition-all"
                                            style={{ width: `${pct}%`, background: t.color }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
        </Card>
    )
}

// ─── Reward Redemption Table ───────────────────────────────────────────────
const PAGE_SIZE = 10

const RedemptionTable = ({ redemptions, accounts, loading }) => {
   const [range, setRange] = useState("all")
    const [customStart, setCustomStart] = useState("")
    const [customEnd, setCustomEnd] = useState("")
    const [statusFilter, setStatusFilter] = useState(null)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)

    const hasCustom = customStart && customEnd
    
    const startISO = useMemo(() => {
        if (customStart && customEnd) {
            return new Date(customStart).toISOString()
        }
        return presetStart(range)
    }, [customStart, customEnd, range])

    const endISO = useMemo(() => {
        if (customStart && customEnd) {
            return new Date(customEnd + "T23:59:59").toISOString()
        }
        return null
    }, [customStart, customEnd])

    // Filter redemptions by date range
    const dateFilteredRedemptions = useMemo(() => {
        if (!redemptions) return []
        return redemptions.filter(r => {
            const d = new Date(r.redeemed_at)
            if (startISO && d < new Date(startISO)) return false
            if (endISO && d > new Date(endISO)) return false
            return true
        })
    }, [redemptions, startISO, endISO])

    // Build membership number lookup from loyalty_id
    const membershipMap = useMemo(() => {
        if (!accounts) return {}
        const map = {}
        for (const acc of accounts) {
            map[acc.loyalty_id] = acc.membership_number
        }
        return map
    }, [accounts])

    const filtered = useMemo(() => {
        if (!dateFilteredRedemptions) return []
        return dateFilteredRedemptions
            .filter(r => !statusFilter || r.redemption_status === statusFilter)
            .filter(r => {
                if (!search) return true
                const q = search.toLowerCase()
                const memberNo = membershipMap[r.loyalty_id] ?? ''
                const rewardName = r.rewards?.reward_name ?? ''
                return memberNo.toLowerCase().includes(q) || rewardName.toLowerCase().includes(q)
            })
    }, [dateFilteredRedemptions, statusFilter, search, membershipMap])

    // Reset to page 1 when filters change
    const handleSearchChange = (val) => { setSearch(val); setPage(1) }
    const handleStatusChange = (val) => { setStatusFilter(val); setPage(1) }
    const handleRangeChange   = (val) => { setPage(1) }

    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const statuses = ['completed', 'pending', 'cancelled', 'expired']

    const statusLabels = {
        completed: 'Used',
        pending: 'Claimed',
        cancelled: 'Cancelled',
        expired: 'Expired'
    }

    const statusColors = { completed: C.green, pending: C.amber, cancelled: C.coral, expired: C.muted }

    const summaryByStatus = useMemo(() => {
        if (!dateFilteredRedemptions) return {}
        return dateFilteredRedemptions.reduce((acc, r) => {
            acc[r.redemption_status] = (acc[r.redemption_status] || 0) + 1
            return acc
        }, {})
    }, [dateFilteredRedemptions])

    const fmtDate = (d) => d
        ? new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
        : '—'

    return (
        <Card>
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                <SectionTitle
                    title="Reward Redemption History"
                    subtitle="Tracked by membership number — click a status filter to narrow results"
                />
                <RangeSelector
                    range={range}
                    setRange={(v) => { setPage(1); setRange(v) }}
                    customStart={customStart}
                    setCustomStart={(v) => { setPage(1); setCustomStart(v) }}
                    customEnd={customEnd}
                    setCustomEnd={(v) => { setPage(1); setCustomEnd(v) }}
                />
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                <KPI label="Total Redemptions" value={dateFilteredRedemptions?.length ?? 0} color={C.blue} loading={loading} />
                <KPI label="Used" value={summaryByStatus.completed ?? 0} color={C.green} loading={loading} />
                <KPI label="Claimed" value={summaryByStatus.pending ?? 0} color={C.amber} loading={loading} />
                <KPI label="Cancelled / Expired"
                    value={(summaryByStatus.cancelled ?? 0) + (summaryByStatus.expired ?? 0)}
                    color={C.coral} loading={loading} />
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 mb-4">
                {/* Search */}
                <div className="relative">
                    <input
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search membership number or reward name…"
                        className="w-full rounded-xl border px-4 py-2 text-xs pr-8 outline-none"
                        style={{ borderColor: C.border, color: C.text, background: C.card }}
                    />
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        fill="none" stroke={C.muted} strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                </div>

                {/* Status filter pills */}
                <div className="flex flex-wrap gap-1.5 items-center">
                    <button onClick={() => handleStatusChange(null)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                        style={{
                            background: statusFilter === null ? C.amber : C.card,
                            borderColor: statusFilter === null ? C.amber : C.border,
                            color: statusFilter === null ? '#fff' : C.textSoft,
                            cursor: 'pointer',
                        }}>All</button>
                    {statuses.map(s => (
                        <button key={s} onClick={() => handleStatusChange(s)}
                            className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                            style={{
                                background: statusFilter === s ? statusColors[s] : C.card,
                                borderColor: statusFilter === s ? statusColors[s] : C.border,
                                color: statusFilter === s ? '#fff' : C.textSoft,
                                cursor: 'pointer',
                            }}>
                            {statusLabels[s]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            {['Membership #', 'Reward', 'Type', 'Points Used', 'Status', 'Redeemed At', 'Completed At'].map(h => (
                                <th key={h} className="text-left py-2 px-2 font-semibold uppercase tracking-wide"
                                    style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.map((r) => (
                            <tr key={r.redemption_id}
                                className="transition-colors"
                                style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td className="py-2.5 px-2">
                                    <span className="font-mono font-bold text-[11px]"
                                        style={{ color: C.amber }}>
                                        {membershipMap[r.loyalty_id] ?? '—'}
                                    </span>
                                </td>
                                <td className="py-2.5 px-2 font-semibold" style={{ color: C.text }}>
                                    {r.rewards?.reward_name ?? '—'}
                                </td>
                                <td className="py-2.5 px-2">
                                    <span className="text-[10px] px-2 py-0.5 rounded capitalize"
                                        style={{ background: C.border, color: C.textSoft }}>
                                        {r.rewards?.reward_logic_type ?? '—'}
                                    </span>
                                </td>
                                <td className="py-2.5 px-2 font-bold" style={{ color: C.blue }}>
                                    {r.points_used != null ? r.points_used.toLocaleString() : '—'}
                                </td>
                                <td className="py-2.5 px-2">
                                    <StatusBadge status={r.redemption_status} />
                                </td>
                                <td className="py-2.5 px-2" style={{ color: C.textSoft }}>
                                    {fmtDate(r.redeemed_at)}
                                </td>
                                <td className="py-2.5 px-2" style={{ color: C.textSoft }}>
                                    {fmtDate(r.completed_at)}
                                </td>
                            </tr>
                        ))}
                        {pageData.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-8 text-center text-xs" style={{ color: C.muted }}>
                                    No redemptions match the current filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <Pagination
                page={page}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
            />
        </Card>
    )
}

// ─── Top Redeemed Rewards Chart ───────────────────────────────────────────
const TopRedeemedRewards = ({ redemptions, loading }) => {
    const { chartData, statusBreakdown } = useMemo(() => {
        if (!redemptions) return { chartData: [], statusBreakdown: [] }

        const rewardMap = {}
        const monthMap  = {}
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

        for (const r of redemptions) {
            const name = r.rewards?.reward_name ?? 'Unknown'
            if (!rewardMap[name]) rewardMap[name] = { name, total: 0, completed: 0, pending: 0, cancelled: 0 }
            rewardMap[name].total++
            if (r.redemption_status === 'completed') rewardMap[name].completed++
            else if (r.redemption_status === 'pending') rewardMap[name].pending++
            else rewardMap[name].cancelled++

            // Monthly trend
            const month = MONTHS[new Date(r.redeemed_at).getMonth()]
            if (!monthMap[month]) monthMap[month] = { month, redemptions: 0 }
            monthMap[month].redemptions++
        }

        const chartData = Object.values(rewardMap)
            .sort((a, b) => b.total - a.total)
            .slice(0, 10)

        const statusBreakdown = Object.values(monthMap).sort((a, b) =>
            MONTHS.indexOf(a.month) - MONTHS.indexOf(b.month)
        )

        return { chartData, statusBreakdown }
    }, [redemptions])

    if (loading) return <Card><SectionTitle title="Top Redeemed Rewards" /><Spinner height={280} /></Card>

    return (
        <Card>
            <SectionTitle
                title="Reward Redemption Analytics"
                subtitle="Most redeemed rewards and monthly trends"
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>
                        Top 10 Most Redeemed Rewards
                    </p>
                    {chartData.length === 0
                        ? <p className="text-xs" style={{ color: C.muted }}>No redemption data yet.</p>
                        : (
                            <ResponsiveContainer debounce={200} width="100%" height={Math.max(220, chartData.length * 32)}>
                                <BarChart data={chartData} layout="vertical"
                                    margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <YAxis type="category" dataKey="name" width={120} interval={0}
                                        tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="completed" name="Completed" fill={C.green} stackId="a" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="pending" name="Unclaimed" fill={C.amber} stackId="a" />
                                    <Bar dataKey="cancelled" name="Cancelled" fill={C.coral} stackId="a" radius={[0, 4, 4, 0]} />
                                    <Legend wrapperStyle={{ fontSize: 10, color: C.textSoft }} />
                                </BarChart>
                            </ResponsiveContainer>
                        )
                    }
                </div>

                <div>
                    <p className="text-[11px] uppercase tracking-wider m-0 mb-2" style={{ color: C.muted }}>
                        Monthly Redemption Volume
                    </p>
                    <ResponsiveContainer debounce={200} width="100%" height={220}>
                        <AreaChart data={statusBreakdown} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="loyaltyGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor={C.amber} stopOpacity={0.4} />
                                    <stop offset="95%" stopColor={C.amber} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.muted, fontSize: 9 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="redemptions" name="Redemptions"
                                stroke={C.amber} fill="url(#loyaltyGrad)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Card>
    )
}

const parseAISections = (text) => {
  if (!text) return []
  const lines = text.split('\n')
  const sections = []
  let current = null
 
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
 
    const isHeader = trimmed.startsWith('### ')
    if (isHeader) {
      if (current) sections.push(current)
      current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
    } else if (/^[-•*]\s+/.test(trimmed)) {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
    } else {
      if (!current) current = { title: null, bullets: [] }
      current.bullets.push(trimmed)
    }
  }
  if (current) sections.push(current)
  return sections
}

const LOYALTY_SECTION_COLORS = {
  'TIER DISTRIBUTION': { color: C.blue, icon: '🏆' },
  'REDEMPTION ACTIVITY': { color: C.purple, icon: '🎁' },
  'REWARD PERFORMANCE': { color: C.teal, icon: '📈' },
  'MEMBER GROWTH': { color: C.green, icon: '👥' },
  'AT-RISK / STALLED TIERS':{ color: C.coral,  icon: '⚠️' },
}
const DEFAULT_SECTION_COLORS = [C.purple, C.teal, C.blue, C.green, C.coral, C.amber]
 
const renderBulletText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
      : part
  )
}
 
const MarkdownBlock = ({ text }) => {
  if (!text) return null
  const sections = parseAISections(text)
  if (!sections.length) return null
 
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sections.map((section, idx) => {
        const key = section.title?.toUpperCase().trim()
        const cfg = (key && LOYALTY_SECTION_COLORS[key]) || {
          color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
          icon: '•',
        }
        return (
          <div key={idx} style={{ borderRadius: 10, border: `1px solid ${cfg.color}28`, overflow: 'hidden' }}>
            {section.title && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                background: cfg.color + '10', borderBottom: `1px solid ${cfg.color}20`,
              }}>
                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.07em', color: cfg.color,
                }}>{section.title}</span>
              </div>
            )}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {section.bullets.map((bullet, bIdx) => (
                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0, marginTop: 7 }} />
                  <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{renderBulletText(bullet)}</span>
                </div>
              ))}
              {section.bullets.length === 0 && (
                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{section.title}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
 
//Sidebar 
const LoyaltyAISidebar = ({
  open, onClose, summary, recommendations, loading, error, onGenerate,
  hasData, history, historyLoading, onDeleteHistory,
}) => {
  const [activeTab, setActiveTab] = useState("analysis") // "analysis" | "history"
  const [expandedId, setExpandedId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
 
  const handleDelete = async (id) => {
    setDeletingId(id)
    await onDeleteHistory(id)
    if (expandedId === id) setExpandedId(null)
    setDeletingId(null)
  }
 
  return (
    <>
      {open && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)", zIndex: 999 }} />
      )}
 
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(420px, 100vw)",
        background: "#fff", borderLeft: `1px solid ${C.border}`, zIndex: 1000,
        display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0, background: C.purple + "06",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", background: C.purple,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
              </svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Loyalty Analyst</p>
              <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Tiers · Redemptions · Reward ROI</p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            borderRadius: 8, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
 
        {/* Tab switcher */}
        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.dark }}>
          {[
            { id: "analysis", label: "Analysis" },
            { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: "10px 0", background: "none", border: "none",
              borderBottom: activeTab === tab.id ? `2px solid ${C.purple}` : "2px solid transparent",
              color: activeTab === tab.id ? C.purple : C.muted,
              fontSize: 12, fontWeight: activeTab === tab.id ? 700 : 400,
              cursor: "pointer", transition: "all 0.15s", marginBottom: -1,
            }}>{tab.label}</button>
          ))}
        </div>
 
        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
          {activeTab === "analysis" && (
            <>
              <button
                onClick={onGenerate}
                disabled={loading || !hasData}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: 50, border: "none",
                  background: loading || !hasData ? C.border : C.purple, color: "#fff",
                  fontSize: 12, fontWeight: 600, cursor: loading || !hasData ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  marginBottom: summary ? 8 : 20, transition: "background 0.2s",
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                      <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Analyzing loyalty data…
                  </>
                ) : summary ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.62"/>
                    </svg>
                    Regenerate Analysis
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                      <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                    </svg>
                    Generate Analysis
                  </>
                )}
              </button>
 
              {summary && !loading && (
                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {["🤖 Gemini 2.5 Flash", "🎁 Based on your loyalty data", "⚠️ For reference only"].map(label => (
                    <span key={label} style={{
                      fontSize: 9, padding: "2px 8px", borderRadius: 50,
                      border: `1px solid ${C.border}`, color: C.muted, background: C.dark,
                    }}>{label}</span>
                  ))}
                </div>
              )}
 
              <div style={{
                padding: "10px 14px", borderRadius: 10, background: C.dark, border: `1px solid ${C.border}`,
                display: "flex", gap: 8, alignItems: "flex-start",
              }}>
                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                  This analysis is produced by Google Gemini 2.5 Flash based on your loyalty
                  program data. Insights are pattern-based interpretations, not verified findings.
                  Validate against your own business judgment before making decisions.
                </p>
              </div>
 
              {error && (
                <div style={{
                  background: C.coral + "15", border: `1px solid ${C.coral}44`, borderRadius: 10,
                  padding: "10px 14px", fontSize: 12, color: C.coral, marginTop: 16,
                }}>{error}</div>
              )}
 
              {!summary && !loading && !error && (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 12, padding: "40px 20px", textAlign: "center",
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%", background: C.purple + "12",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                      <path fill={C.purple} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>
                      Loyalty Management Interpretation
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, maxWidth: 260, lineHeight: 1.6 }}>
                      Click <strong>Generate Analysis</strong> to get a plain-language reading of tier health, redemption behavior, and reward ROI.
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", marginTop: 4 }}>
                    {[
                      { icon: "🏆", label: "Tier distribution health" },
                      { icon: "🎁", label: "Redemption trends" },
                      { icon: "📈", label: "Top-performing rewards" },
                      { icon: "⚠️", label: "At-risk / stalled members" },
                    ].map(item => (
                      <div key={item.label} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                        borderRadius: 8, background: C.dark, border: `1px solid ${C.border}`,
                      }}>
                        <span style={{ fontSize: 14 }}>{item.icon}</span>
                        <span style={{ fontSize: 12, color: C.textSoft }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
 
              {loading && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 20px" }}>
                  <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                    <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                    <path fill={C.purple} d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your loyalty data…</p>
                </div>
              )}
 
              {summary && !loading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ background: C.purple + "08", border: `1px solid ${C.purple}28`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{
                      padding: "10px 14px", borderBottom: `1px solid ${C.purple}18`,
                      background: C.purple + "10", display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ fontSize: 14 }}>🎁</span>
                      <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.purple }}>
                        What the Data Shows
                      </p>
                    </div>
                    <div style={{ padding: "12px 14px" }}><MarkdownBlock text={summary} /></div>
                  </div>
 
                  {recommendations && (
                    <div style={{ background: C.teal + "08", border: `1px solid ${C.teal}28`, borderRadius: 12, overflow: "hidden" }}>
                      <div style={{
                        padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`,
                        background: C.teal + "10", display: "flex", alignItems: "center", gap: 8,
                      }}>
                        <span style={{ fontSize: 14 }}>🔮</span>
                        <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                          Outlook & What to Anticipate
                        </p>
                      </div>
                      <div style={{ padding: "12px 14px" }}><MarkdownBlock text={recommendations} /></div>
                    </div>
                  )}
 
                  <div style={{
                    padding: "10px 14px", borderRadius: 10, background: C.amber + "08",
                    border: `1px solid ${C.amber}22`, display: "flex", gap: 8, alignItems: "flex-start",
                  }}>
                    <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
                    <p style={{ margin: 0, fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
                      This analysis reflects the filters active when it was generated. If you've changed the date range, click <strong>Regenerate</strong> for a fresh read.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
 
          {activeTab === "history" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {historyLoading ? (
                <div className="flex items-center justify-center gap-2" style={{ height: 160 }}>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                    <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : !history?.length ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "40px 20px", textAlign: "center" }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%", background: C.muted + "18",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
                  }}>🕓</div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                  <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                    Generated analyses will be saved here automatically.
                  </p>
                </div>
              ) : history.map((entry) => {
                const isOpen = expandedId === entry.id
                const generatedDate = new Date(entry.generated_at)
                return (
                  <div key={entry.id} style={{
                    borderRadius: 12, border: `1px solid ${isOpen ? C.purple + "44" : C.border}`,
                    overflow: "hidden", transition: "border-color 0.15s",
                  }}>
                    <div
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 14px", background: isOpen ? C.purple + "08" : C.dark,
                        cursor: "pointer", gap: 10, transition: "background 0.15s",
                      }}
                      onClick={() => setExpandedId(isOpen ? null : entry.id)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: C.purple,
                            background: C.purple + "15", borderRadius: 50, padding: "2px 8px",
                          }}>
                            {entry.scope_label || "Loyalty Management"}
                          </span>
                          <span style={{
                            fontSize: 10, color: C.textSoft, background: C.border + "80",
                            borderRadius: 50, padding: "2px 8px",
                          }}>
                            {entry.date_range}
                          </span>
                        </div>
                        <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                          {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                          {" at "}
                          {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                          disabled={deletingId === entry.id}
                          title="Delete this entry"
                          style={{
                            background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                            padding: "3px 6px", cursor: deletingId === entry.id ? "not-allowed" : "pointer",
                            color: C.muted, fontSize: 11, display: "flex", alignItems: "center",
                            opacity: deletingId === entry.id ? 0.5 : 1,
                          }}
                        >
                          {deletingId === entry.id ? (
                            <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                              <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4"/>
                              <path fill={C.muted} d="M4 12a8 8 0 018-8v8z"/>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                          )}
                        </button>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2"
                          style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </div>
                    </div>
 
                    {isOpen && (
                      <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ background: C.purple + "06", borderRadius: 10, border: `1px solid ${C.purple}18`, overflow: "hidden" }}>
                          <div style={{
                            padding: "7px 12px", borderBottom: `1px solid ${C.purple}14`,
                            background: C.purple + "0E", display: "flex", alignItems: "center", gap: 6,
                          }}>
                            <span style={{ fontSize: 12 }}>🎁</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                              What the Data Shows
                            </span>
                          </div>
                          <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.summary} /></div>
                        </div>
 
                        {entry.recommendations && (
                          <div style={{ background: C.teal + "06", borderRadius: 10, border: `1px solid ${C.teal}18`, overflow: "hidden" }}>
                            <div style={{
                              padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`,
                              background: C.teal + "0E", display: "flex", alignItems: "center", gap: 6,
                            }}>
                              <span style={{ fontSize: 12 }}>🔮</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                Outlook & What to Anticipate
                              </span>
                            </div>
                            <div style={{ padding: "10px 12px" }}><MarkdownBlock text={entry.recommendations} /></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const LoyaltyAIFloatingButton = ({ onClick, hasNew }) => (
  <button
    onClick={onClick}
    title="Open Loyalty Analyst"
    style={{
      position: "fixed", bottom: 28, right: 28, width: 52, height: 52, borderRadius: "50%",
      background: C.purple, border: "none", cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 18px rgba(124,58,237,0.35)", zIndex: 998,
      transition: "transform 0.15s, box-shadow 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(124,58,237,0.45)" }}
    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(124,58,237,0.35)" }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1"/>
    </svg>
    {hasNew && (
      <span style={{ position: "absolute", top: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: C.amber, border: "2px solid #fff" }} />
    )}
  </button>
)

export default function LoyaltyAnalytics() {
    const { tiers, accounts, redemptions, rewards } = useLoyaltyData()
    const loading = tiers === null || accounts === null || redemptions === null
 
    const [aiSummary, setAiSummary] = useState(null)
    const [aiRecommendations, setAiRecommendations] = useState(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false)
    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useLoyaltyAIHistory()

    const handleGenerateInsights = async () => {
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiRecommendations(null)
 
        try {
            const totalMembers = accounts?.length ?? 0
            const activeMembers = accounts?.filter(a => a.is_active).length ?? 0
 
            const tierCounts = {}
            for (const acc of accounts ?? []) {
                if (!acc.current_tier_id) continue
                tierCounts[acc.current_tier_id] = (tierCounts[acc.current_tier_id] || 0) + 1
            }
            const tierBreakdown = (tiers ?? []).map(t => ({
                name: t.tier_name,
                count: tierCounts[t.tier_id] || 0,
                minPoints: t.points_required,
            }))
 
            const totalRedemptions = redemptions?.length ?? 0
            const byStatus = (redemptions ?? []).reduce((acc, r) => {
                acc[r.redemption_status] = (acc[r.redemption_status] || 0) + 1
                return acc
            }, {})
 
            const rewardCounts = {}
            for (const r of redemptions ?? []) {
                const name = r.rewards?.reward_name ?? "Unknown"
                rewardCounts[name] = (rewardCounts[name] || 0) + 1
            }
            const topRewards = Object.entries(rewardCounts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
 
            const payload = {
                totalMembers,
                activeMembers,
                tierBreakdown,
                totalRedemptions,
                redemptionsByStatus: byStatus,
                topRewards,
            }
 
            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/summarize-loyalty`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ ...payload, mode: "interpret_and_forecast" }),
            })
            if (!res.ok) throw new Error('Failed to generate insights')
            const { summary, recommendations } = await res.json()
 
            setAiSummary(summary)
            setAiRecommendations(recommendations)
 
            const { data, error } = await saveAnalysis({
                summary,
                recommendations,
                scopeLabel: "Loyalty Management",
                dateRange: "All Time",
            })
            if (error) {
                console.error('Failed to save AI history:', error)
                setAiError(prev => prev ?? `Analysis generated but not saved: ${error.message}`)
            }
        } catch (err) {
            setAiError(err.message || 'Failed to generate analysis. Please try again.')
        } finally {
            setAiLoading(false)
        }
    }
 
    return (
        <div className="flex flex-col gap-5">
            <TierDistributionPanel tiers={tiers} accounts={accounts} loading={loading} />
            <TopRedeemedRewards redemptions={redemptions} loading={loading} />
            <RedemptionTable redemptions={redemptions} accounts={accounts} loading={loading} />
 
            <LoyaltyAISidebar
                open={aiSidebarOpen}
                onClose={() => setAiSidebarOpen(false)}
                summary={aiSummary}
                recommendations={aiRecommendations}
                loading={aiLoading}
                error={aiError}
                onGenerate={handleGenerateInsights}
                hasData={!loading}
                history={history}
                historyLoading={historyLoading}
                onDeleteHistory={deleteAnalysis}
            />
            <LoyaltyAIFloatingButton
                onClick={() => setAiSidebarOpen(true)}
                hasNew={!!aiSummary}
            />
        </div>
    )
}